import React, { useCallback, useRef, useId } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useReducedMotion } from '../../hooks/useMediaQuery';
import { useAnchoredPortal } from '../../hooks/useAnchoredPortal';
import { usePopoverDisclosure } from '../../hooks/usePopoverDisclosure';
import { personaChatPath } from '../../utils/personaChatPath';
import { NewChatPickerPanel } from './NewChatPickerPanel';
import type { Persona } from '../../types/persona';

interface NewChatPickerProps {
  trigger: (args: { open: () => void; isOpen: boolean }) => React.ReactNode;
  initialSelectedTags?: string[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSelect?: (persona: Persona) => void;
  /** 'launch' (default) = recents + footer nav + navigate-on-select; 'select' = neither, onSelect-driven. */
  variant?: 'launch' | 'select';
  /** Opt-in route on commit when no onSelect. Defaults to true for 'launch', false for 'select'. */
  navigateOnSelect?: boolean;
}

const PANEL_W = 520;

export const NewChatPicker: React.FC<NewChatPickerProps> = ({
  trigger, initialSelectedTags, open: controlledOpen, onOpenChange, onSelect,
  variant = 'launch', navigateOnSelect,
}) => {
  const isSelect = variant === 'select';
  const { darkMode } = useTheme();
  const reducedMotion = useReducedMotion();
  const navigate = useNavigate();
  const uid = useId();

  const triggerRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // The trigger is a display:contents span wrapper; the real layout/focus element is its child.
  const getTriggerEl = () => (triggerRef.current?.firstElementChild as HTMLElement | null);

  const { isOpen, setOpen, toggle } = usePopoverDisclosure({
    open: controlledOpen, onOpenChange, getTriggerEl, panelRef,
  });
  const placement = useAnchoredPortal({
    getAnchorEl: getTriggerEl, open: isOpen, width: PANEL_W, align: 'right', preferOpenUp: false,
  });

  const handleSelect = useCallback((persona: Persona) => {
    if (onSelect) onSelect(persona);
    else if (navigateOnSelect ?? variant === 'launch') navigate(personaChatPath(persona.id));
    setOpen(false, { skipFocusReturn: true }); // navigate moves focus to the chat input
  }, [onSelect, navigateOnSelect, variant, navigate, setOpen]);

  const listboxId = `ncp-listbox-${uid}`;
  const titleId = `ncp-title-${uid}`;

  const panel = isOpen && placement ? (
    <div
      ref={panelRef}
      data-testid="new-chat-picker-panel"
      style={{ ...placement.style, zIndex: 9999 }}
      className={[
        'rounded-lg shadow-lg border-l-4 overflow-hidden',
        darkMode ? 'bg-gray-800 border-emerald-600 text-gray-100' : 'bg-white border-emerald-500 text-gray-900',
        reducedMotion ? '' : 'animate-fadeIn',
      ].join(' ')}
    >
      <NewChatPickerPanel
        onClose={() => setOpen(false)}
        onSelect={handleSelect}
        initialSelectedTags={initialSelectedTags}
        listboxId={listboxId}
        titleId={titleId}
        showRecents={!isSelect}
        showFooterNav={!isSelect}
        maxHeight={placement.maxHeight}
      />
    </div>
  ) : null;

  return (
    <span ref={triggerRef} style={{ display: 'contents' }}>
      {trigger({ open: toggle, isOpen })}
      {panel && createPortal(panel, document.body)}
    </span>
  );
};
